import { FacebookPixel, type EventData } from "react-meta-pixel";
import { initConsent } from "./consent";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Les outils publicitaires (bannière CookieYes, pixel Meta, GTM) ne se chargent que si
 * `VITE_TRACKING_ENABLED` vaut `true` (production) : les parcours de test ne doivent pas
 * alimenter les conversions.
 */
export const TRACKING_ENABLED =
  import.meta.env.VITE_TRACKING_ENABLED === "true";

const pixel = TRACKING_ENABLED
  ? new FacebookPixel({
      pixelID: import.meta.env.VITE_PIXEL_ID,
      debug: false,
      pageViewOnInit: false,
      autoConfig: false,
      disablePushState: true,
    })
  : null;

if (pixel) {
  // Le pixel démarre muet et n'envoie qu'après le consentement publicitaire du visiteur.
  window.fbq?.("consent", "revoke");
  pixel.init({});
  initConsent((consent) => {
    window.fbq?.("consent", consent.advertisement ? "grant" : "revoke");
  });
}

/**
 * Évènement standard Meta. `eventID` doit être stable et dérivé des données (identifiant
 * de compte, de commande, d'intent Stripe) : Meta s'en sert pour dédoublonner l'envoi
 * navigateur et l'envoi serveur de l'API Conversions.
 */
export function trackMetaEvent<K extends keyof EventData>(
  eventName: K,
  eventID: string | undefined,
  data?: EventData[K],
) {
  pixel?.trackEvent(eventName, data, eventID ? { eventID } : undefined);
}

export function trackMetaCustomEvent(
  eventName: string,
  eventID: string | undefined,
  data?: Record<string, unknown>,
) {
  if (!pixel) return;
  window.fbq?.(
    "trackCustom",
    eventName,
    data,
    eventID ? { eventID } : undefined,
  );
}
